import React from "react";
import {
  Alert,
  Link,
  SpaceBetween,
} from "@amzn/awsui-components-console";
import { Link as ReactRouterLink } from "react-router-dom";

import { ACCESS_REQUEST_URL } from "src/app/constants";
import { JSON_FORMS_ROUTES } from "src/jsonForms/constants";

interface AccessDeniedAlertProps {
  /**
   * When `true`, additionally render a link back to the LOB selector at
   * `/json-forms`. Used when the user has access to at least one other
   * LOB and is asking for an LOB they cannot access (or for an unknown
   * `<lob>` URL parameter).
   */
  hasOtherLobs?: boolean;
}

/**
 * Access-denied alert rendered by `JsonFormsClaimIntakeApp` when the user has
 * no LOB roles or navigates to a LOB they cannot access. The alert
 * always provides a link to the access-request ticket template; when
 * `hasOtherLobs` is `true` it also offers a link back to the LOB
 * selector so the user can continue with an LOB they do have access to.
 */
function AccessDeniedAlert({
  hasOtherLobs = false,
}: AccessDeniedAlertProps): JSX.Element {
  return (
    <Alert
      type="error"
      header="Access denied"
      data-testid="access-denied-alert"
    >
      <SpaceBetween size="s" direction="vertical">
        <span>
          You do not have the required role to access this claim intake flow.
          Please request access to continue.
        </span>
        <Link href={ACCESS_REQUEST_URL} target="_blank" external>
          Request access
        </Link>
        {hasOtherLobs ? (
          <ReactRouterLink to={JSON_FORMS_ROUTES.LOB_SELECTOR}>
            Back to claim type selection
          </ReactRouterLink>
        ) : null}
      </SpaceBetween>
    </Alert>
  );
}

export default AccessDeniedAlert;
